/**
 * Shared video UI: progress helpers (CSS clip-path pie + optional SVG ring dashoffset),
 * global `theme-video-playback-progress` custom element, and `video-cards-player`.
 */
(function () {
  'use strict';

  /**
   * @param {number | null | undefined} unitProgress
   * @returns {number} clamped to [0, 1]
   */
  function clampUnitProgress(unitProgress) {
    if (unitProgress == null || Number.isNaN(unitProgress)) {
      return 0;
    }
    return Math.max(0, Math.min(1, unitProgress));
  }

  /**
   * Maps normalized playback progress (0–1) to a CSS clip-path polygon that sweeps a pie wedge,
   * for use with `clip-path` on a pseudo-element (e.g. pause icon progress fill).
   *
   * @param {number} unitProgress
   * @returns {string} CSS `polygon(...)` value
   */
  function normalizedPlaybackProgressToPieClipPathCss(unitProgress) {
    const clamped = clampUnitProgress(unitProgress);
    if (clamped <= 0) {
      return 'polygon(50% 50%, 0 0, 0 0, 0 0, 0 0, 0 0)';
    }
    const corners = ['0 0', '100% 0', '100% 100%', '0 100%'];
    const scaled = clamped * 4;
    const fullCorners = Math.floor(scaled);
    const edgeFraction = scaled - fullCorners;
    const points = ['50% 50%', '0 0'];
    for (let cornerIndex = 1; cornerIndex <= fullCorners && cornerIndex < 4; cornerIndex++) {
      points.push(corners[cornerIndex]);
    }
    if (edgeFraction > 0.001 && fullCorners < 4) {
      const startCorner = corners[fullCorners].split(' ').map((token) => (token === '100%' ? 100 : 0));
      const endCorner = corners[(fullCorners + 1) % 4].split(' ').map((token) => (token === '100%' ? 100 : 0));
      const xPercent = startCorner[0] + (endCorner[0] - startCorner[0]) * edgeFraction;
      const yPercent = startCorner[1] + (endCorner[1] - startCorner[1]) * edgeFraction;
      points.push(xPercent + '% ' + yPercent + '%');
    }
    while (points.length < 6) {
      points.push(points[points.length - 1]);
    }
    return 'polygon(' + points.join(', ') + ')';
  }

  /**
   * Updates `--progress-ring-offset` on an SVG circle used as a stroke-dasharray ring.
   *
   * @param {SVGElement | null} svgRingElement
   * @param {number} circumferencePx full circle length in px (2 * Math.PI * radius)
   * @param {number} unitProgress 0–1
   */
  function applyStrokeDashoffsetFromUnitProgress(svgRingElement, circumferencePx, unitProgress) {
    if (!svgRingElement || circumferencePx == null || !isFinite(circumferencePx)) {
      return;
    }
    const clamped = clampUnitProgress(unitProgress);
    const offsetPx = circumferencePx * (1 - clamped);
    svgRingElement.style.setProperty('--progress-ring-offset', String(offsetPx));
  }

  /**
   * Drives a callback on each animation frame with the latest unit progress from a reader function.
   * Stops when `stop()` is called.
   *
   * @param {{ getUnitProgress: () => number | null, onUnitProgress: (unitProgress: number) => void }} options
   * @returns {{ start: () => void, stop: () => void }}
   */
  function createPlaybackProgressSampler(options) {
    const getUnitProgress = options.getUnitProgress;
    const onUnitProgress = options.onUnitProgress;
    let requestAnimationFrameId = null;

    function tick() {
      const unitProgress = getUnitProgress();
      if (unitProgress != null && isFinite(unitProgress)) {
        onUnitProgress(unitProgress);
      }
      requestAnimationFrameId = window.requestAnimationFrame(tick);
    }

    return {
      start: function () {
        if (requestAnimationFrameId != null) {
          return;
        }
        requestAnimationFrameId = window.requestAnimationFrame(tick);
      },
      stop: function () {
        if (requestAnimationFrameId != null) {
          window.cancelAnimationFrame(requestAnimationFrameId);
          requestAnimationFrameId = null;
        }
      },
    };
  }

  /**
   * @param {string | null | undefined} cssValue
   * @returns {number | null}
   */
  function parseCssLengthPixels(cssValue) {
    if (!cssValue || typeof cssValue !== 'string') {
      return null;
    }
    const trimmed = cssValue.trim();
    if (!trimmed || trimmed === 'auto') {
      return null;
    }
    const withoutPx = trimmed.endsWith('px') ? trimmed.slice(0, -2) : trimmed;
    const parsed = Number(withoutPx);
    return Number.isFinite(parsed) ? parsed : null;
  }

  /**
   * Circumference for the optional progress ring: prefer SVG circle `r`, then CSS variable, else theme default.
   *
   * @param {Element | null} progressRingElement
   * @returns {number | null} null when there is no ring element
   */
  function resolveRingCircumferencePixels(progressRingElement) {
    const defaultRadiusPixels = 52;
    if (!progressRingElement) {
      return null;
    }
    const circleElement = progressRingElement.querySelector('circle');
    if (circleElement) {
      const radiusValue = Number(circleElement.getAttribute('r'));
      if (isFinite(radiusValue) && radiusValue > 0) {
        return 2 * Math.PI * radiusValue;
      }
    }
    const inlineCircumference =
      progressRingElement.style && progressRingElement.style.getPropertyValue('--progress-ring-circumference');
    const fromInline = parseCssLengthPixels(inlineCircumference);
    if (fromInline != null && isFinite(fromInline) && fromInline > 0) {
      return fromInline;
    }
    let computedCircumference = '';
    try {
      computedCircumference = window
        .getComputedStyle(progressRingElement)
        .getPropertyValue('--progress-ring-circumference');
    } catch (error) {
      computedCircumference = '';
    }
    const fromComputed = parseCssLengthPixels(computedCircumference);
    if (fromComputed != null && isFinite(fromComputed) && fromComputed > 0) {
      return fromComputed;
    }
    return 2 * Math.PI * defaultRadiusPixels;
  }

  window.themeVideoProgressUi = {
    clampUnitProgress,
    normalizedPlaybackProgressToPieClipPathCss,
    applyStrokeDashoffsetFromUnitProgress,
    createPlaybackProgressSampler,
  };

  class ThemeVideoPlaybackProgress extends HTMLElement {
    constructor() {
      super();
      this.videoElement = null;
      this.boundaryElement = null;
      this.clipTargetElement = null;
      this.progressRingElement = null;
      this.ringCircumferencePixels = null;
      this.playbackSampler = null;
      this.deferredVideoMutationObserver = null;
      this.videoListenersAttached = false;
      this.boundVideoPlay = this.onVideoPlay.bind(this);
      this.boundVideoPause = this.onVideoPause.bind(this);
      this.boundVideoEnded = this.onVideoEnded.bind(this);
    }

    connectedCallback() {
      const themeVideoProgressUi = window.themeVideoProgressUi;
      if (!themeVideoProgressUi) {
        return;
      }
      const boundarySelector = this.dataset.videoBoundary;
      const videoTargetSelector = this.dataset.videoTarget;
      const clipTargetSelector = this.dataset.clipTarget;
      const ringTargetSelector = this.dataset.progressRingTarget;

      if (!boundarySelector || !videoTargetSelector || !clipTargetSelector) {
        return;
      }

      this.boundaryElement = this.closest(boundarySelector);
      if (!this.boundaryElement) {
        return;
      }

      this.clipTargetElement = this.boundaryElement.querySelector(clipTargetSelector);
      this.progressRingElement = ringTargetSelector
        ? this.boundaryElement.querySelector(ringTargetSelector)
        : null;

      if (!this.clipTargetElement) {
        return;
      }

      this.ringCircumferencePixels = resolveRingCircumferencePixels(this.progressRingElement);

      if (
        this.progressRingElement &&
        this.ringCircumferencePixels != null &&
        isFinite(this.ringCircumferencePixels)
      ) {
        this.progressRingElement.style.setProperty(
          '--progress-ring-circumference',
          String(this.ringCircumferencePixels)
        );
        themeVideoProgressUi.applyStrokeDashoffsetFromUnitProgress(
          this.progressRingElement,
          this.ringCircumferencePixels,
          0
        );
      }

      this.clipTargetElement.style.setProperty(
        '--video-progress-clip',
        themeVideoProgressUi.normalizedPlaybackProgressToPieClipPathCss(0)
      );

      this.videoElement = this.boundaryElement.querySelector(videoTargetSelector);
      if (this.videoElement) {
        this.attachVideoListenersAndSyncPlayback();
      } else {
        this.startDeferredVideoBindingObserver(videoTargetSelector);
      }
    }

    startDeferredVideoBindingObserver(videoTargetSelector) {
      if (this.deferredVideoMutationObserver || !this.boundaryElement) {
        return;
      }
      const boundaryElement = this.boundaryElement;
      const self = this;
      this.deferredVideoMutationObserver = new MutationObserver(function () {
        const candidateVideo = boundaryElement.querySelector(videoTargetSelector);
        if (candidateVideo && candidateVideo.nodeName === 'VIDEO') {
          self.videoElement = candidateVideo;
          self.deferredVideoMutationObserver.disconnect();
          self.deferredVideoMutationObserver = null;
          self.attachVideoListenersAndSyncPlayback();
        }
      });
      this.deferredVideoMutationObserver.observe(this.boundaryElement, { childList: true, subtree: true });
    }

    attachVideoListenersAndSyncPlayback() {
      if (!this.videoElement || this.videoListenersAttached) {
        return;
      }
      this.videoElement.addEventListener('play', this.boundVideoPlay);
      this.videoElement.addEventListener('pause', this.boundVideoPause);
      this.videoElement.addEventListener('ended', this.boundVideoEnded);
      this.videoListenersAttached = true;
      if (!this.videoElement.paused) {
        this.onVideoPlay();
      }
    }

    detachVideoListeners() {
      if (!this.videoElement || !this.videoListenersAttached) {
        return;
      }
      this.videoElement.removeEventListener('play', this.boundVideoPlay);
      this.videoElement.removeEventListener('pause', this.boundVideoPause);
      this.videoElement.removeEventListener('ended', this.boundVideoEnded);
      this.videoListenersAttached = false;
    }

    disconnectedCallback() {
      this.stopSampler();
      if (this.deferredVideoMutationObserver) {
        this.deferredVideoMutationObserver.disconnect();
        this.deferredVideoMutationObserver = null;
      }
      this.detachVideoListeners();
      this.videoElement = null;
      this.boundaryElement = null;
      this.clipTargetElement = null;
      this.progressRingElement = null;
      this.ringCircumferencePixels = null;
    }

    stopSampler() {
      if (this.playbackSampler) {
        this.playbackSampler.stop();
        this.playbackSampler = null;
      }
    }

    resetProgressVisualsToZero() {
      const themeVideoProgressUi = window.themeVideoProgressUi;
      if (!themeVideoProgressUi || !this.clipTargetElement) {
        return;
      }
      this.clipTargetElement.style.setProperty(
        '--video-progress-clip',
        themeVideoProgressUi.normalizedPlaybackProgressToPieClipPathCss(0)
      );
      if (
        this.progressRingElement &&
        this.ringCircumferencePixels != null &&
        isFinite(this.ringCircumferencePixels)
      ) {
        themeVideoProgressUi.applyStrokeDashoffsetFromUnitProgress(
          this.progressRingElement,
          this.ringCircumferencePixels,
          0
        );
      }
    }

    startSampler() {
      const themeVideoProgressUi = window.themeVideoProgressUi;
      if (!themeVideoProgressUi || !this.videoElement || !this.clipTargetElement) {
        return;
      }
      this.stopSampler();
      const videoElement = this.videoElement;
      const clipTargetElement = this.clipTargetElement;
      const progressRingElement = this.progressRingElement;
      const ringCircumferencePixels = this.ringCircumferencePixels;

      this.playbackSampler = themeVideoProgressUi.createPlaybackProgressSampler({
        getUnitProgress: function () {
          if (!videoElement.duration || !isFinite(videoElement.duration)) {
            return null;
          }
          return videoElement.currentTime / videoElement.duration;
        },
        onUnitProgress: function (unitProgress) {
          const clipPathCss = themeVideoProgressUi.normalizedPlaybackProgressToPieClipPathCss(unitProgress);
          clipTargetElement.style.setProperty('--video-progress-clip', clipPathCss);
          if (progressRingElement && ringCircumferencePixels != null && isFinite(ringCircumferencePixels)) {
            themeVideoProgressUi.applyStrokeDashoffsetFromUnitProgress(
              progressRingElement,
              ringCircumferencePixels,
              unitProgress
            );
          }
        },
      });
      this.playbackSampler.start();
    }

    onVideoPlay() {
      this.startSampler();
    }

    onVideoPause() {
      this.stopSampler();
      this.resetProgressVisualsToZero();
    }

    onVideoEnded() {
      this.stopSampler();
      this.resetProgressVisualsToZero();
    }
  }

  if (!customElements.get('theme-video-playback-progress')) {
    customElements.define('theme-video-playback-progress', ThemeVideoPlaybackProgress);
  }

  class VideoCardsPlayer extends HTMLElement {
    constructor() {
      super();
      this.blocks = [];
      this.currentBlockIndex = null;
      this.boundEnded = this.onVideoEnded.bind(this);
      this._viewportAutoplayActive = false;
    }

    connectedCallback() {
      const blockElements = this.querySelectorAll('.video-cards__block[data-video-url]');
      blockElements.forEach((blockElement, blockIndex) => {
        const ratioElement = blockElement.querySelector('.video-cards__block-ratio');
        if (!ratioElement) return;
        const videoElement = ratioElement.querySelector('.video-cards__block-ratio__video');
        const controlElement = blockElement.querySelector('.video-cards__block-ratio__control');
        const posterUrl = blockElement.dataset.posterUrl || '';
        const videoUrl = blockElement.dataset.videoUrl || '';
        if (!videoElement || !videoUrl) return;
        videoElement.src = videoUrl;
        if (posterUrl) videoElement.poster = posterUrl;
        const clickTarget = controlElement || ratioElement;
        this.blocks.push({ blockElement, videoElement, controlElement, ratioElement });
        clickTarget.addEventListener('click', () => this.toggleBlock(blockIndex));
      });
      this.setupViewportAutoplay();
    }

    setupViewportAutoplay() {
      if (!this.blocks.length || !('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) return;
          if (entry.isIntersecting) {
            if (this.currentBlockIndex !== null) this.pauseBlock(this.currentBlockIndex);
            this._viewportAutoplayActive = true;
            this.playBlock(0);
          } else {
            if (this._viewportAutoplayActive && this.currentBlockIndex === 0) {
              this.pauseBlock(0);
            }
            this._viewportAutoplayActive = false;
          }
        },
        { rootMargin: '0px', threshold: 0.2 }
      );
      observer.observe(this);
    }

    toggleBlock(blockIndex) {
      this._viewportAutoplayActive = false;
      if (this.currentBlockIndex === blockIndex) {
        const block = this.blocks[blockIndex];
        if (block.videoElement.paused) {
          this.playBlock(blockIndex);
        } else {
          this.pauseBlock(blockIndex);
        }
      } else {
        if (this.currentBlockIndex !== null) this.pauseBlock(this.currentBlockIndex);
        this.playBlock(blockIndex);
      }
    }

    playBlock(blockIndex) {
      const block = this.blocks[blockIndex];
      if (!block) return;
      if (this.currentBlockIndex !== null && this.currentBlockIndex !== blockIndex) {
        this.pauseBlock(this.currentBlockIndex);
      }
      block.videoElement.muted = true;
      block.videoElement.currentTime = 0;
      block.videoElement.play().catch(() => {});
      block.videoElement.classList.add('is-active');
      if (block.controlElement) {
        block.controlElement.classList.add('is-playing');
        block.controlElement.setAttribute('aria-label', 'Pause video');
      }
      block.ratioElement.querySelector('.video-cards__block-ratio__img')?.classList.add('is-hidden');
      this.currentBlockIndex = blockIndex;
      block.videoElement.addEventListener('ended', this.boundEnded);
    }

    pauseBlock(blockIndex) {
      const block = this.blocks[blockIndex];
      if (!block) return;
      block.videoElement.pause();
      block.videoElement.classList.remove('is-active');
      block.videoElement.removeEventListener('ended', this.boundEnded);
      if (block.controlElement) {
        block.controlElement.classList.remove('is-playing');
        block.controlElement.setAttribute('aria-label', 'Play video');
      }
      block.ratioElement.querySelector('.video-cards__block-ratio__img')?.classList.remove('is-hidden');
      if (this.currentBlockIndex === blockIndex) this.currentBlockIndex = null;
    }

    onVideoEnded() {
      const currentIndex = this.currentBlockIndex;
      if (currentIndex === null) return;
      this.pauseBlock(currentIndex);
      const nextIndex = currentIndex + 1;
      if (nextIndex < this.blocks.length) {
        this.playBlock(nextIndex);
      }
    }
  }

  if (!customElements.get('video-cards-player')) {
    customElements.define('video-cards-player', VideoCardsPlayer);
  }
})();
